import { useState, useEffect, useRef } from 'react';
import EntitlementsService from '../services/EntitlementsService';
import { getIndexedReadableAttributeIdsFromPerEntityResponse, getSearchableAttributeIdsFromPerEntityResponse, canEditFromPerEntityResponse } from '../types/EntityEntitlements';
import { extractErrorMessage } from '../utils/apiUtils';

function readTier2DeleteEntity(response: unknown): boolean {
    if (response == null || typeof response !== 'object') return false;
    const r = response as any;
    const value = r.DeleteEntity ?? r.deleteEntity ?? r.deleteentity ?? r.DELETEENTITY;
    return value === true;
}

export interface UsePerEntityTableEntitlementOptions {
    /** Schema entity id for the current table (e.g. 'group', 'shared', 'employee'). */
    entityType: string | undefined;
    authResolved: boolean;
    /** When this changes, per-entity entitlement is refetched (e.g. on-behalf-of toggled). */
    onBehalfOfKey?: string;
    /**
     * When true, only fetch and block load when entityType is set (e.g. PersonManagement on #Person/shared but not All Users).
     * When false, always fetch for entityType and block until resolved.
     */
    onlyWhenEntityTypeSet?: boolean;
    /** Called when entitlement is resolved so the consumer can reload the table. Stored in a ref so it can be updated without re-subscribing. */
    onResolved?: () => void;
}

export interface UsePerEntityTableEntitlementResult {
    /** null = loading, undefined = no filter (admin or no data), string[] = only these attributes. */
    allowedTableAttributeIds: string[] | undefined | null;
    /** null = loading, undefined = no filter (admin or no data), string[] = searchable attributes from tier2. */
    allowedSearchAttributeIds: string[] | undefined | null;
    /** Ref for use in loadData and shouldSkipLoad (avoids stale closures). */
    allowedTableAttributeIdsRef: React.MutableRefObject<string[] | undefined | null>;
    /** Return true to skip table load until entitlement is resolved. */
    shouldSkipLoad: () => boolean;
    /** When true, show row pencil icon to go straight to edit (from tier2 EditEntity). */
    canEditRow: boolean;
    /** When true, show create button (from tier2 CreateEntity). null = tier2 not loaded yet. */
    canCreateFromTier2: boolean | null;
    /** When true, allow bulk delete selection for this entity type (from tier2 DeleteEntity). null = tier2 not loaded yet. */
    canDeleteFromTier2: boolean | null;
    /** When true, entity type is disjointed in tier2 (show * next to table title). */
    isDisjointed: boolean;
    /** When set, the per-entity (tier2) entitlements request failed; show this message in the UI. */
    tier2Error: string | null;
}

/** Log when a non-canonical entity type is passed; entitlement API expects singular ids (e.g. "journalentry"). */
function warnIfPluralEntityType(key: string): void {
    const k = key.trim().toLowerCase();
    if (k !== 'journalentry' && k.includes('journalentr')) {
        console.error('usePerEntityTableEntitlement: entityType should use canonical singular id "journalentry". Fix the caller.');
    }
}

/**
 * Centralized hook for per-entity table entitlement (GET /mgmt/Entitlements/entity/{schemaEntityId}).
 * Use in any management component that loads a table and should filter requested attributes by entitlement.
 * - Subscribes to main entitlement changes (e.g. on-behalf-of toggle) and refetches per-entity when they complete.
 * - Fetches on mount and when entityType, authResolved, or onBehalfOfKey change.
 * - Returns allowed attribute ids and shouldSkipLoad so the table waits for entitlement before loading.
 *
 * Consumers: DynamicTableView (all entity types using it), PersonManagement (entity-specific Person views),
 * useGroupsData (Groups), useLocalityManagement (Class, Building, School, ResponsibilityCenter). Locality
 * tables do not filter requested attributes by entitlement (their search APIs use a fixed list); the hook
 * still runs so the per-entity entitlement API is called and the table reloads when on-behalf-of changes.
 */
export function usePerEntityTableEntitlement(
    options: UsePerEntityTableEntitlementOptions
): UsePerEntityTableEntitlementResult {
    const {
        entityType,
        authResolved,
        onBehalfOfKey,
        onlyWhenEntityTypeSet = false,
        onResolved
    } = options;

    const [allowedTableAttributeIds, setAllowedTableAttributeIds] = useState<string[] | undefined | null>(
        onlyWhenEntityTypeSet && !entityType ? undefined : null
    );
    const [allowedSearchAttributeIds, setAllowedSearchAttributeIds] = useState<string[] | undefined | null>(
        onlyWhenEntityTypeSet && !entityType ? undefined : null
    );
    const [canEditRow, setCanEditRow] = useState(false); // hide pencil until tier2 EditEntity is true
    const [canCreateFromTier2, setCanCreateFromTier2] = useState<boolean | null>(null);
    const [canDeleteFromTier2, setCanDeleteFromTier2] = useState<boolean | null>(null);
    const [isDisjointed, setIsDisjointed] = useState(false);
    const [tier2Error, setTier2Error] = useState<string | null>(null);
    const allowedTableAttributeIdsRef = useRef<string[] | undefined | null>(allowedTableAttributeIds);
    const onResolvedRef = useRef(onResolved);
    const entityTypeRef = useRef(entityType);
    const authResolvedRef = useRef(authResolved);

    useEffect(() => { allowedTableAttributeIdsRef.current = allowedTableAttributeIds; }, [allowedTableAttributeIds]);
    useEffect(() => { onResolvedRef.current = onResolved; }, [onResolved]);
    useEffect(() => { entityTypeRef.current = entityType; }, [entityType]);
    useEffect(() => { authResolvedRef.current = authResolved; }, [authResolved]);

    // When main entity entitlements change (e.g. on-behalf-of toggled), refetch per-entity for current view
    useEffect(() => {
        const onMainEntitlementsChanged = () => {
            if (!authResolvedRef.current) return;
            const key = entityTypeRef.current;
            if (onlyWhenEntityTypeSet && !key) return;
            if (!key?.trim()) return;
            const k = key.trim().toLowerCase();
            warnIfPluralEntityType(key);
            if (k === 'entity') {
                setAllowedTableAttributeIds(undefined);
                allowedTableAttributeIdsRef.current = undefined;
                setAllowedSearchAttributeIds(undefined);
                return;
            }
            setAllowedTableAttributeIds(null);
            allowedTableAttributeIdsRef.current = null;
            setAllowedSearchAttributeIds(null);
            EntitlementsService.fetchEntityEntitlementForSchemaEntity(k)
                .then((response) => {
                    setTier2Error(null);
                    const tableValue = getIndexedReadableAttributeIdsFromPerEntityResponse(response ?? null);
                    const searchValue = getSearchableAttributeIdsFromPerEntityResponse(response ?? null);
                    setAllowedTableAttributeIds(tableValue);
                    allowedTableAttributeIdsRef.current = tableValue;
                    setAllowedSearchAttributeIds(searchValue);
                    setCanEditRow(canEditFromPerEntityResponse(response ?? null));
                    setCanCreateFromTier2(response?.CreateEntity === true ? true : response != null ? false : null);
                    setCanDeleteFromTier2(response != null ? readTier2DeleteEntity(response) : null);
                    setIsDisjointed(response?.Entity?.Disjointed === true);
                })
                .catch((err) => {
                    setTier2Error(extractErrorMessage(err) || 'Entitlements request failed');
                    setAllowedTableAttributeIds(undefined);
                    allowedTableAttributeIdsRef.current = undefined;
                    setAllowedSearchAttributeIds(undefined);
                    setCanEditRow(false);
                    setCanCreateFromTier2(null);
                    setCanDeleteFromTier2(null);
                    setIsDisjointed(false);
                })
                .finally(() => { onResolvedRef.current?.(); });
        };
        const unsubscribe = EntitlementsService.subscribe(onMainEntitlementsChanged);
        return unsubscribe;
    }, [onlyWhenEntityTypeSet]);

    // Fetch on mount and when entityType / authResolved / onBehalfOfKey change (use cache when already present to avoid duplicate network calls).
    // When !authResolved we keep state as null (loading) so that when auth resolves the effect re-runs and we attempt the tier2 call (e.g. journal entry table).
    useEffect(() => {
        if (!authResolved) {
            setAllowedTableAttributeIds(null);
            allowedTableAttributeIdsRef.current = null;
            setAllowedSearchAttributeIds(null);
            return;
        }
        if (onlyWhenEntityTypeSet && !entityType?.trim()) {
            setAllowedTableAttributeIds(undefined);
            allowedTableAttributeIdsRef.current = undefined;
            setAllowedSearchAttributeIds(undefined);
            setCanDeleteFromTier2(null);
            return;
        }
        const rawKey = (entityType ?? '').trim();
        if (!rawKey || rawKey.toLowerCase() === 'entity') {
            setAllowedTableAttributeIds(undefined);
            allowedTableAttributeIdsRef.current = undefined;
            setAllowedSearchAttributeIds(undefined);
            setCanDeleteFromTier2(null);
            return;
        }
        warnIfPluralEntityType(rawKey);
        const key = rawKey.toLowerCase();
        const cached = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(key);
        if (cached != null) {
            setTier2Error(null);
            const tableValue = getIndexedReadableAttributeIdsFromPerEntityResponse(cached);
            const searchValue = getSearchableAttributeIdsFromPerEntityResponse(cached);
            setAllowedTableAttributeIds(tableValue);
            allowedTableAttributeIdsRef.current = tableValue;
            setAllowedSearchAttributeIds(searchValue);
            setCanEditRow(canEditFromPerEntityResponse(cached));
            setCanCreateFromTier2(cached.CreateEntity === true ? true : false);
            setCanDeleteFromTier2(readTier2DeleteEntity(cached));
            setIsDisjointed(cached?.Entity?.Disjointed === true);
            onResolvedRef.current?.();
            return;
        }
        let cancelled = false;
        setAllowedTableAttributeIds(null);
        allowedTableAttributeIdsRef.current = null;
        setAllowedSearchAttributeIds(null);
        EntitlementsService.fetchEntityEntitlementForSchemaEntity(key)
            .then((response) => {
                if (cancelled) return;
                setTier2Error(null);
                const tableValue = getIndexedReadableAttributeIdsFromPerEntityResponse(response ?? null);
                const searchValue = getSearchableAttributeIdsFromPerEntityResponse(response ?? null);
                setAllowedTableAttributeIds(tableValue);
                allowedTableAttributeIdsRef.current = tableValue;
                setAllowedSearchAttributeIds(searchValue);
                setCanEditRow(canEditFromPerEntityResponse(response ?? null));
                setCanCreateFromTier2(response?.CreateEntity === true ? true : response != null ? false : null);
                setCanDeleteFromTier2(response != null ? readTier2DeleteEntity(response) : null);
                setIsDisjointed(response?.Entity?.Disjointed === true);
            })
            .catch((err) => {
                if (cancelled) return;
                setTier2Error(extractErrorMessage(err) || 'Entitlements request failed');
                setAllowedTableAttributeIds(undefined);
                allowedTableAttributeIdsRef.current = undefined;
                setAllowedSearchAttributeIds(undefined);
                setCanEditRow(false);
                setCanCreateFromTier2(null);
                setCanDeleteFromTier2(null);
                setIsDisjointed(false);
            })
            .finally(() => {
                if (!cancelled) onResolvedRef.current?.();
            });
        return () => { cancelled = true; };
    }, [authResolved, entityType, onBehalfOfKey, onlyWhenEntityTypeSet]);

    const shouldSkipLoad = () => {
        if (onlyWhenEntityTypeSet && !entityTypeRef.current) return false;
        return allowedTableAttributeIdsRef.current === null;
    };

    return {
        allowedTableAttributeIds,
        allowedSearchAttributeIds,
        allowedTableAttributeIdsRef,
        shouldSkipLoad,
        canEditRow,
        canCreateFromTier2,
        canDeleteFromTier2,
        isDisjointed,
        tier2Error
    };
}
